import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile(user?.id);

  // Show loader while auth or profile is loading
  if (authLoading || profileLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Only show pending approval after profile has finished loading AND profile exists but is not approved
  if (profile && !profile.approved) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-accent p-4">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold mb-4">Account Pending Approval</h1>
          <p className="text-muted-foreground">
            Your account is awaiting approval from an administrator. You'll receive
            an email once your account has been approved.
          </p>
        </div>
      </div>
    );
  }

  // If user exists but profile is still null after loading, keep showing loader
  // This handles the race condition where profile hasn't been created yet
  if (!profile) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <>{children}</>;
}